import "./index.css";
import { ToastProvider } from "./components/ui/toast";
import { Toaster } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route } from "react-router-dom";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import MisspelledWord from "./pages/data-analysis/MisspelledWord";
import GlobalSearch from "./pages/data-analysis/GlobalSearch";
import SynonymWord from "./pages/data-analysis/SynonymWord";
import ReferenceBook from "./pages/data-analysis/ReferenceBook";
import ManageStockIndia from "./pages/shopping-cart/ManageStockIndia";
import ManageStockUSA from "./pages/shopping-cart/ManageStockUSA";
import ManageStockSpecialUSA from "./pages/shopping-cart/ManageStockSpecialUSA";
import ManageStockOther from "./pages/shopping-cart/ManageStockOther";
import ManageAddress from "./pages/shopping-cart/ManageAddress";
import OrderManagement from "./pages/shopping-cart/OrderManagement";
import PurchaseOrders from "./pages/shopping-cart/PurchaseOrders";
import ShippingManagement from "./pages/shopping-cart/ShippingManagement";
import Committee from "./pages/content/Committee";
import AboutUs from "./pages/content/AboutUs";
import ContactUs from "./pages/content/ContactUs";
import Banner from "./pages/content/Banner";
import FeaturedBooks from "./pages/content/FeaturedBooks";
import DonateContent from "./pages/content/DonateContent";
import TermsContent from "./pages/content/TermsContent";
import PrivacyPolicyContent from "./pages/content/PrivacyPolicyContent";
import HomeHero from "./pages/content/HomeHero";
import FooterContent from "./pages/content/FooterContent";
import ExportElibrary from "./pages/exports/ExportElibrary";
import ExportBook from "./pages/exports/ExportBook";
import ExportArticle from "./pages/exports/ExportArticle";
import UserProfile from "./pages/UserProfile";
import ChangePassword from "./pages/ChangePassword";
import NotFound from "./pages/NotFound";
import ExportMagazine from "./pages/exports/ExportMagazine";
import ExportAgam from "./pages/exports/ExportAgam";
import ExportManuscript from "./pages/exports/ExportManuscript";
import ExportAudio from "./pages/exports/ExportAudio";
import UpdateDownloadCount from "./pages/users/UpdateDownloadCount";
import UpdateUsersData from "./pages/users/UpdateUsersData";
import UserRegistration from "./pages/users/UserRegistration";
import UserDownload from "./pages/users/UserDownload";
import ExportUsers from "./pages/users/ExportUsers";
import MergeData from "./pages/users/MergeData";

import AdminProtectedRouter from "./components/AdminProtectedRouter";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ToastProvider />
      <Toaster />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/signup" element={<Signup />} />

        {/* Protected Admin Routes */}
        <Route element={<AdminProtectedRouter />}>
          <Route path="/dashboard" element={<Dashboard />} />

          {/* User Routes */}
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/change-password" element={<ChangePassword />} />

          {/* User Related Routes */}
          <Route
            path="/users/update-download-count"
            element={<UpdateDownloadCount />}
          />
          <Route path="/users/update-data" element={<UpdateUsersData />} />
          <Route path="/users/registration" element={<UserRegistration />} />
          <Route path="/users/download" element={<UserDownload />} />
          <Route path="/users/export" element={<ExportUsers />} />
          <Route path="/users/merge-data" element={<MergeData />} />

          {/* Export Routes */}
          <Route path="/export-elibrary" element={<ExportElibrary />} />
          <Route path="/export-book" element={<ExportBook />} />
          <Route path="/export-article" element={<ExportArticle />} />
          <Route path="/export-magazine" element={<ExportMagazine />} />
          <Route path="/export-agam" element={<ExportAgam />} />
          <Route path="/export-manuscript" element={<ExportManuscript />} />
          <Route path="/export-audio" element={<ExportAudio />} />

          {/* Data Analysis Routes */}
          <Route
            path="/data-analysis/global-search"
            element={<GlobalSearch />}
          />
          <Route
            path="/data-analysis/misspelled-word"
            element={<MisspelledWord />}
          />
          <Route path="/data-analysis/synonym-word" element={<SynonymWord />} />
          <Route
            path="/data-analysis/reference-book"
            element={<ReferenceBook />}
          />

          {/* Shopping Cart Routes */}
          <Route
            path="/shopping-cart/manage-stock-india"
            element={<ManageStockIndia />}
          />
          <Route
            path="/shopping-cart/manage-stock-usa"
            element={<ManageStockUSA />}
          />
          <Route
            path="/shopping-cart/manage-stock-special-usa"
            element={<ManageStockSpecialUSA />}
          />
          <Route
            path="/shopping-cart/manage-stock-other"
            element={<ManageStockOther />}
          />
          <Route
            path="/shopping-cart/manage-address"
            element={<ManageAddress />}
          />
          <Route
            path="/shopping-cart/order-management"
            element={<OrderManagement />}
          />
          <Route
            path="/shopping-cart/purchase-orders"
            element={<PurchaseOrders />}
          />
          <Route
            path="/shopping-cart/shipping-management"
            element={<ShippingManagement />}
          />

          {/* Content Management Routes */}
          <Route path="/content/about-us" element={<AboutUs />} />
          <Route path="/content/committee" element={<Committee />} />
          <Route path="/content/donate" element={<DonateContent />} />
          <Route path="/content/terms" element={<TermsContent />} />
          <Route
            path="/content/privacy-policy"
            element={<PrivacyPolicyContent />}
          />
          <Route path="/content/contact" element={<ContactUs />} />
          <Route path="/content/banner" element={<Banner />} />
          <Route path="/content/home-hero" element={<HomeHero />} />
          <Route path="/content/footer" element={<FooterContent />} />
          <Route path="/content/featured-books" element={<FeaturedBooks />} />
        </Route>

        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
